import type { AnalysisMode, AnalysisResponse } from '../types';

export interface ShortlistHistoryEntry {
  id: string;
  timestamp: number;
  mode: AnalysisMode;
  jd: string;
  title: string;
  jobLabel: string;
  cvCount: number;
  results: AnalysisResponse;
}

const STORAGE_KEY = 'shortlist_history';
const MAX_ENTRIES = 20;

export const loadHistory = (): ShortlistHistoryEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const persist = (entries: ShortlistHistoryEntry[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage full or unavailable — history just won't persist this time.
  }
};

export const addHistoryEntry = (entries: ShortlistHistoryEntry[], entry: ShortlistHistoryEntry): ShortlistHistoryEntry[] => {
  const next = [entry, ...entries].slice(0, MAX_ENTRIES);
  persist(next);
  return next;
};

export const removeHistoryEntry = (entries: ShortlistHistoryEntry[], id: string): ShortlistHistoryEntry[] => {
  const next = entries.filter(e => e.id !== id);
  persist(next);
  return next;
};

export const clearHistory = (): ShortlistHistoryEntry[] => {
  persist([]);
  return [];
};

export const exportHistory = (entries: ShortlistHistoryEntry[]): void => {
  const payload = JSON.stringify({ version: 1, exportedAt: Date.now(), history: entries }, null, 2);
  const blob = new Blob([payload], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `shortlist-history-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

export const importHistory = (jsonText: string, current: ShortlistHistoryEntry[]): ShortlistHistoryEntry[] => {
  const parsed = JSON.parse(jsonText);
  const incoming: ShortlistHistoryEntry[] = Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed?.history)
    ? parsed.history
    : [];
  const existingIds = new Set(current.map(e => e.id));
  const merged = [
    ...current,
    ...incoming.filter(e => e?.id && !existingIds.has(e.id)),
  ]
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, MAX_ENTRIES);
  persist(merged);
  return merged;
};

export const historyTitle = (entry: ShortlistHistoryEntry): string => {
  if (entry.mode === 'jd') {
    const trimmed = entry.jd.trim();
    if (!trimmed) return 'Job Description';
    return trimmed.length > 46 ? `${trimmed.slice(0, 46)}…` : trimmed;
  }
  if (entry.mode === 'title') {
    return entry.title.trim() || 'Job Title';
  }
  return 'General Analysis';
};

export const formatHistoryTime = (timestamp: number): string => {
  const diffMs = Date.now() - timestamp;
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export const modeLabel = (mode: AnalysisMode): string => {
  if (mode === 'jd') return 'Job Description';
  if (mode === 'title') return 'Job Title';
  return 'General';
};
